/**
 * Admin modal for editing per-account service info (container size,
 * collection frequency, next pickup, waste stream, notes, address).
 * These values are manual overrides — anything set here WINS over Hub
 * / Zoho data. Used as a fallback until the ZMEX Hub team ships full
 * service-profile support for phone-only customers and multi-yard
 * customers like BCN.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  X, Truck, Loader2, RotateCcw, Save, CheckCircle2, AlertCircle,
} from 'lucide-react';

import { adminApi } from '@/lib/api';

const FIELDS = [
  { key: 'container_size',       label: 'Container size',       placeholder: 'e.g. 20 yd rolloff' },
  { key: 'collection_frequency', label: 'Collection frequency', placeholder: 'e.g. Weekly · Mon/Thu' },
  { key: 'next_collection_at',   label: 'Next scheduled pickup', placeholder: 'e.g. 2026-08-05 or Mon Aug 5' },
  { key: 'waste_stream',         label: 'Waste stream',         placeholder: 'e.g. Mixed C&D' },
  { key: 'address',              label: 'Service address',      placeholder: 'Overrides Zoho address if set' },
  { key: 'service_notes',        label: 'Service notes',        placeholder: 'e.g. Lock code 4213, back gate only', textarea: true },
];

export default function AdminServiceInfoModal({ customer, onClose, onSaved }) {
  const accountId = customer.account_id;
  const [state, setState] = useState({ current: {}, manual: {}, has_manual_override: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [flash, setFlash] = useState(null);
  const [draft, setDraft] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setFlash(null);
    try {
      const { data } = await adminApi.getServiceInfo(accountId);
      setState(data);
      setDraft(data.manual || {});
    } catch (err) {
      setFlash({ type: 'error', msg: err?.detail || 'Failed to load service info.' });
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setFlash(null);
    try {
      // For fields the admin cleared, we send empty string to unset.
      const body = {};
      FIELDS.forEach(({ key }) => {
        const before = state.manual?.[key] || '';
        const after = draft[key] || '';
        if (before !== after) body[key] = after;
      });
      if (Object.keys(body).length === 0) {
        setFlash({ type: 'success', msg: 'No changes to save.' });
        setSaving(false);
        return;
      }
      const { data } = await adminApi.patchServiceInfo(accountId, body);
      setState(data);
      setDraft(data.manual || {});
      setFlash({ type: 'success', msg: 'Saved. Customer will see these values on their next dashboard load.' });
      onSaved?.();
    } catch (err) {
      setFlash({ type: 'error', msg: err?.detail || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (resetting) return;
    if (!window.confirm('Clear ALL manual overrides for this account? The dashboard will revert to whatever the Hub / Zoho returns.')) return;
    setResetting(true);
    setFlash(null);
    try {
      await adminApi.resetServiceInfo(accountId);
      await load();
      setFlash({ type: 'success', msg: 'Cleared. Hub / Zoho values will show again.' });
      onSaved?.();
    } catch (err) {
      setFlash({ type: 'error', msg: err?.detail || 'Reset failed.' });
    } finally {
      setResetting(false);
    }
  };

  const dirty = FIELDS.some(({ key }) => (state.manual?.[key] || '') !== (draft[key] || ''));

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid="admin-service-info-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !saving && !resetting) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#EDE5D4]">
          <div className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-[#0B1930]" />
            <div>
              <h2 className="font-heading uppercase tracking-wide text-base text-[#0B1930]">
                Service Info
              </h2>
              <p className="text-[11px] text-[#4B5563]">
                {customer.business_name} · Zoho {customer.zoho_customer_id || '—'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving || resetting}
            className="text-[#4B5563] hover:text-[#0B1930] disabled:opacity-40"
            data-testid="admin-service-info-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1">
          {loading && (
            <div className="text-sm text-[#4B5563] flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading current values…
            </div>
          )}

          {!loading && (
            <>
              {flash && (
                <div
                  className={`mb-3 rounded-xl px-3 py-2 text-sm flex items-start gap-2 ${
                    flash.type === 'success'
                      ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
                      : 'bg-red-50 border border-red-200 text-red-800'
                  }`}
                  data-testid="admin-service-info-flash"
                >
                  {flash.type === 'success'
                    ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                    : <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />}
                  <span>{flash.msg}</span>
                </div>
              )}

              <div className="mb-4 rounded-xl bg-[#FAF7F0] border border-[#EDE5D4] px-3 py-2.5 text-[11px] text-[#4B5563]">
                Anything you type here <span className="font-semibold text-[#0B1930]">overrides</span> the Hub &amp; Zoho values for this customer only. Leave a field empty to fall back to whatever the Hub / Zoho returns.
                {state.has_manual_override && (
                  <span className="ml-1 inline-block text-emerald-700 font-semibold">Manual override active.</span>
                )}
              </div>

              <div className="space-y-4">
                {FIELDS.map(({ key, label, placeholder, textarea }) => {
                  const overriding = (draft[key] || '') !== '';
                  const currentDisplay = state.current?.[key] || '';
                  return (
                    <div key={key}>
                      <label className="block text-[11px] uppercase tracking-wide text-[#4B5563] mb-1">
                        {label}
                        {overriding && (
                          <span className="ml-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-1.5 py-0.5 text-[9px] font-bold uppercase">Override</span>
                        )}
                      </label>
                      {textarea ? (
                        <textarea
                          rows={2}
                          value={draft[key] || ''}
                          onChange={(e) => setField(key, e.target.value)}
                          placeholder={placeholder}
                          className="w-full rounded-xl bg-white border border-[#EDE5D4] px-3 py-2 text-sm text-[#0B1930] focus:outline-none focus:border-[#C8A46B]"
                          data-testid={`admin-service-info-input-${key}`}
                        />
                      ) : (
                        <input
                          type="text"
                          value={draft[key] || ''}
                          onChange={(e) => setField(key, e.target.value)}
                          placeholder={placeholder}
                          className="w-full rounded-full bg-white border border-[#EDE5D4] px-3 py-2 text-sm text-[#0B1930] focus:outline-none focus:border-[#C8A46B]"
                          data-testid={`admin-service-info-input-${key}`}
                        />
                      )}
                      {currentDisplay && currentDisplay !== (draft[key] || '') && (
                        <p className="text-[10px] text-[#9CA3AF] mt-1 truncate">
                          Currently rendered on dashboard: <span className="font-semibold">{currentDisplay}</span>
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Customer self-service contact info (read-only for staff) */}
              <div
                className="mt-6 rounded-xl border border-[#EDE5D4] bg-[#FAF7F0] px-4 py-3"
                data-testid="admin-customer-contact-info"
              >
                <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[#4B5563]">
                  Customer-submitted contact info
                </p>
                {state.customer_contact_info &&
                (state.customer_contact_info.phone ||
                  state.customer_contact_info.billing_email ||
                  state.customer_contact_info.mailing_address) ? (
                  <dl className="space-y-1.5 text-sm text-[#0B1930]">
                    {state.customer_contact_info.phone && (
                      <div className="flex justify-between gap-3">
                        <dt className="text-[#4B5563]">Phone</dt>
                        <dd className="font-semibold">{state.customer_contact_info.phone}</dd>
                      </div>
                    )}
                    {state.customer_contact_info.billing_email && (
                      <div className="flex justify-between gap-3">
                        <dt className="text-[#4B5563]">Invoice email</dt>
                        <dd className="font-semibold break-all">{state.customer_contact_info.billing_email}</dd>
                      </div>
                    )}
                    {state.customer_contact_info.mailing_address && (
                      <div className="flex justify-between gap-3">
                        <dt className="text-[#4B5563]">Mailing address</dt>
                        <dd className="font-semibold text-right">{state.customer_contact_info.mailing_address}</dd>
                      </div>
                    )}
                    {state.customer_contact_info.updated_at && (
                      <p className="pt-1 text-[10px] text-[#9CA3AF]">
                        Submitted {new Date(state.customer_contact_info.updated_at).toLocaleString()}
                      </p>
                    )}
                  </dl>
                ) : (
                  <p className="text-xs text-[#9CA3AF]">No customer-submitted info yet.</p>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-[#EDE5D4] bg-[#FAF7F0]">
          <button
            type="button"
            onClick={resetAll}
            disabled={loading || saving || resetting || !state.has_manual_override}
            className="inline-flex items-center gap-1 rounded-full bg-white border border-red-200 hover:bg-red-50 text-red-700 px-3 py-2 text-[11px] font-bold uppercase tracking-wide transition-colors disabled:opacity-40"
            data-testid="admin-service-info-reset"
          >
            {resetting ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
            Reset all
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving || resetting}
              className="rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wide text-[#0B1930] hover:bg-[#EDE5D4] transition-colors disabled:opacity-60"
              data-testid="admin-service-info-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={loading || saving || resetting || !dirty}
              className="inline-flex items-center gap-2 rounded-full bg-[#0B1930] hover:bg-[#111A2E] text-white px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors disabled:opacity-60"
              data-testid="admin-service-info-save"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
